import { DEFAULT_PROXY_TARGET } from './proxy';

/**
 * Service URLs.
 *
 * Each service is a relative segment (`/catalog`, …) hung off `API_BASE`,
 * resolved from `VITE_API_BASE`:
 *
 *  - Local dev: leave `VITE_API_BASE` unset — paths stay relative and route
 *    through the Vite proxy (see vite.config.ts). You can instead set it in
 *    `.env.local` (e.g. `https://api.dev.ordermesh.io`) to hit a backend
 *    directly, but that only works if the backend returns CORS headers for
 *    the dev origin (http://localhost:3001) — the proxy exists to avoid that.
 *  - Deployed: CI sets `VITE_API_BASE` per environment to the API host
 *    (`https://api.<env>.ordermesh.io`) in the "Build and Deploy" step of
 *    .github/workflows/portal-cicd.yml, so the bundle targets that host.
 *  - If unset, paths are relative/same-origin.
 */
const API_BASE = (
  (import.meta.env as Record<string, string | undefined>).VITE_API_BASE ?? ''
).replace(/\/+$/, '');

/**
 * The API host as an absolute URL, for systems outside the browser, such as
 * the host a Snippet builds a store's webhook delivery URLs on. Under the Vite
 * dev server there is no `VITE_API_BASE`; its relative paths go through the dev
 * proxy, so the target of the notification segment, which receives those
 * deliveries, stands in. Any other build without `VITE_API_BASE` gets '', so
 * the Snippet refuses rather than wiring a store to the dev host.
 */
const notificationProxyTarget = (
  (import.meta.env as Record<string, string | undefined>).VITE_NOTIFICATION_PROXY_TARGET || DEFAULT_PROXY_TARGET
).replace(/\/+$/, '');

export const apiBaseUrl = API_BASE || (import.meta.env.DEV ? notificationProxyTarget : '');

const path = (segment: string): string => `${API_BASE}/${segment}`;

export const services = {
  ai: path('ai'),
  catalog: path('catalog'),
  history: path('history'),
  insight: path('insight'),
  integration: path('integration'),
  merchant: path('merchant'),
  notification: path('notification'),
  inventory: path('inventory'),
  template: path('template'),
  order: path('order'),
  workflow: path('workflow'),
  routing: path('routing'),
  shipment: path('shipment'),
  transformation: path('transformation'),
  user: path('user'),
  vendor: path('vendor'),
  files: path('files'),
  config: path('config'),
  note: path('note'),
  carrier: path('carrier'),
  sku: path('sku'),
  sse: path('sse'),
  portal: path('portal'),
  pricing: path('pricing'),
} as const;

export type ServiceKey = keyof typeof services;
